import { some } from "lodash"
import { LicenseStatusEnum } from "../../../features/shared/utils"
import { hasActiveSignedDisclosure } from "../../../features/providerProfile/Disclosure/DisclosureV2"
import { FileStatusDisplayIdEnum } from "../../Files/schema"
import { LiabilityInsurance, Provider } from "../../Provider/schema"
import {
  ProviderProfileStep,
  ProviderStepNameEnum,
  StepStatusEnum,
} from "../schema"
import { LiabilityInsuranceStatusEnum } from "../../Practice/schema"

const PRIMARY = "primary"
const TELEHEALTH = "telehealth"
const OTHER = "other"

export const personalInfoContactComplete = (provider: Provider): boolean => {
  const providerHasPhone = provider.providerPhones.some(
    (phone) => !!phone.number && !!phone.phoneTypeDisplayId,
  )
  const providerHasPrimaryEmail = provider.providerEmails.some(
    (email) => !!email.address && email.emailTypeDisplayId === PRIMARY,
  )

  return (
    !!provider.firstName &&
    !!provider.lastName &&
    providerHasPhone &&
    providerHasPrimaryEmail
  )
}

export const personInfoDetailsComplete = (provider: Provider): boolean => {
  return !!provider.gender
}

export const getPersonalInfoStepStatus = (
  provider: Provider,
): StepStatusEnum => {
  const isPersonalInfoComplete =
    personalInfoContactComplete(provider) && personInfoDetailsComplete(provider)

  return isPersonalInfoComplete ? StepStatusEnum.COMPLETE : StepStatusEnum.READY
}

export const clientFocusSpecialtiesComplete = (provider: Provider): boolean => {
  const providerHasPrimarySpecialty = provider.providerSpecialties.some(
    (specialty) => specialty.specialtyTypeDisplayId === PRIMARY,
  )
  return providerHasPrimarySpecialty
}

export const clientFocusDemographicsComplete = (provider: Provider) => {
  const providerHasAgesSeen = provider.providerAgesSeenDisplayIds.length > 0
  const providerHasLanguges = provider.providerLanguageDisplayIds.length > 0
  return providerHasAgesSeen && providerHasLanguges
}

export const clientFocusSessionsComplete = (provider: Provider) => {
  const providerSelectedTelehealthSession =
    provider.providerSessionTypeDisplayIds.includes(TELEHEALTH)
  const providerHasOtherTelehealthPlatform =
    provider.providerTelehealthPlatforms.some(
      (platform) =>
        platform.telehealthPlatformDisplayId === OTHER &&
        !!platform.otherPlatformName,
    )
  const providerHasValidTelehealth =
    providerHasOtherTelehealthPlatform ||
    provider.providerTelehealthPlatforms.length > 0

  return !providerSelectedTelehealthSession || providerHasValidTelehealth
}

export const getClientFocusStepStatus = (
  provider: Provider,
): StepStatusEnum => {
  const isClientFocusComplete =
    clientFocusSpecialtiesComplete(provider) &&
    clientFocusDemographicsComplete(provider) &&
    clientFocusSessionsComplete(provider)

  return isClientFocusComplete ? StepStatusEnum.COMPLETE : StepStatusEnum.READY
}

export const getAvailabilityStepStatus = (
  provider: Provider,
): StepStatusEnum => {
  const providerHasDaysOfOperations =
    provider.providerAvailableDisplayIds.length > 0

  const isAvailabilityComplete = providerHasDaysOfOperations

  return isAvailabilityComplete ? StepStatusEnum.COMPLETE : StepStatusEnum.READY
}

export const getQualificationsStepStatus = (
  provider: Provider,
): StepStatusEnum => {
  const providerHasQualifications = provider.providerLicenses.some(
    (license) =>
      !!license.licenseStatusDisplayId &&
      [LicenseStatusEnum.ACTIVE, LicenseStatusEnum.EXPIRING_SOON].includes(
        license.licenseStatusDisplayId as LicenseStatusEnum,
      ) &&
      license.fileStatusDisplayId === FileStatusDisplayIdEnum.AVAILABLE,
  )
  return providerHasQualifications
    ? StepStatusEnum.COMPLETE
    : StepStatusEnum.READY
}

export const getInsuranceStepStatus = (provider: Provider): StepStatusEnum => {
  const providerHasInsurance = provider.isInsurancePaneled
    ? provider.providerInsuranceDisplayIds.length > 0
    : true
  return providerHasInsurance ? StepStatusEnum.COMPLETE : StepStatusEnum.READY
}

export const getServiceStepStatus = (provider: Provider): StepStatusEnum => {
  // User has either attested to virtual or not during the onboarding process if the isVirtualAttested field is non-null/undefined
  const providerVirtualAttestationComplete =
    typeof provider.isVirtualAttested === "boolean"
  return providerVirtualAttestationComplete
    ? StepStatusEnum.COMPLETE
    : StepStatusEnum.READY
}

export const getLocationsStepStatus = (provider: Provider): StepStatusEnum => {
  const providerHasLocations = provider.providerLocations.length > 0
  // TODO: Remove this code once migration is complete.
  const areAny = provider.providerLocations.some(
    (location) => location.createdBy === window._env_.MIGRATION_SERVICE_USER_ID,
  )
  if (areAny) return StepStatusEnum.READY
  return providerHasLocations ? StepStatusEnum.COMPLETE : StepStatusEnum.READY
}

export const getLiabilityInsuranceStepStatus = (
  provider: Provider,
): StepStatusEnum => {
  const filteredAuthReleases = provider.providerAuthReleases.filter(
    (authRelease) =>
      !!authRelease.fileId &&
      !authRelease.archivedBy &&
      !authRelease.archivedDateTime &&
      authRelease.fileStatusDisplayId === FileStatusDisplayIdEnum.AVAILABLE,
  )
  const hasValidAuthRelease = filteredAuthReleases.length > 0

  const providerHasLiabilityInsurance = some(
    provider.liabilityInsurances,
    (pi: LiabilityInsurance) => {
      const isLiabilityInsuranceCurrent = [
        LiabilityInsuranceStatusEnum.PENDING,
        LiabilityInsuranceStatusEnum.ACTIVE,
        LiabilityInsuranceStatusEnum.EXPIRING_SOON,
      ].includes(
        pi.liabilityInsuranceStatusDisplayId as LiabilityInsuranceStatusEnum,
      )
      const isLiabilityInsuranceFileAvailable =
        pi.fileStatusDisplayId === FileStatusDisplayIdEnum.AVAILABLE
      return isLiabilityInsuranceCurrent && isLiabilityInsuranceFileAvailable
    },
  )
  const isLiabilityInsuranceStepComplete =
    hasValidAuthRelease && providerHasLiabilityInsurance
  return isLiabilityInsuranceStepComplete
    ? StepStatusEnum.COMPLETE
    : StepStatusEnum.READY
}

export const getDisclosureStepStatus = (provider: Provider): StepStatusEnum => {
  return hasActiveSignedDisclosure(provider)
    ? StepStatusEnum.COMPLETE
    : StepStatusEnum.READY
}

export const isProviderComplete = (
  provider: Provider,
  isNoVirtualLocationEnabled?: boolean,
): boolean => {
  const providerProfileStepStatuses = [
    getPersonalInfoStepStatus(provider),
    getClientFocusStepStatus(provider),
    getAvailabilityStepStatus(provider),
    getQualificationsStepStatus(provider),
    getInsuranceStepStatus(provider),
    // The Services page is hidden whenever the FF is false OR undefined (not configured).
    // Only require services attestation when the FF is explicitly true (page is accessible).
    // This mirrors the nav hide logic: hidden: !IsNoVirtualLocationEnabled
    ...(isNoVirtualLocationEnabled ? [getServiceStepStatus(provider)] : []),
    getDisclosureStepStatus(provider),
  ]

  return providerProfileStepStatuses.every(
    (status) => status === StepStatusEnum.COMPLETE,
  )
}

export const getProviderProfileStep = (
  providerProfileSteps: ProviderProfileStep[],
  stepName: ProviderStepNameEnum,
) => {
  return providerProfileSteps.find(
    (providerProfileStep) => providerProfileStep.name === stepName,
  ) as ProviderProfileStep
}
